import BaseChart from "@/classes/BaseChart";
import ProjectWidgetApi from "@/classes/ProjectWidgetApi";
import WidgetApi from "@/classes/WidgetApi";
import { isoKey, mStart, qStart, toDate, wStart } from "@/helpers/timeBuckets";
import { chartsSettings } from "@/settings/charts";
import { otherValues } from "@/settings/constants";
import { ProjectOutputFilterParams } from "@/settings/projectOutputFilters";
import { ChartDataRecord } from "@/types/charts";
import { Project } from "@/types/project";

export type DualSourceSpec = {
  metricKey: string;
  metric: string | string[];
  source: "plan" | "explore";
};

const asMetrics = (metric: string | string[]): string[] =>
  (Array.isArray(metric) ? metric : [metric]).filter((m): m is string => typeof m === "string" && m.length > 0);

export type DualComposeArgs = {
  chart: BaseChart;
  project: Project | null;
  fetchExplore: (body: unknown) => Promise<unknown>;
  dimKey: string;
  exploreMetricSlot: string;
  primary: DualSourceSpec;
  secondary: DualSourceSpec;
  isOvertime?: boolean;
  filters?: ProjectOutputFilterParams | null;
  groupKey?: string;
};

const STRIPPABLE_METRIC_KEYS = [
  "yAxis",
  "yAxisPrimary",
  "yAxisSecondary",
  "xAxis",
  "xAxisPrimary",
  "xAxisSecondary",
  "displayMetric",
  "displayMetricSecondary",
];

const stripChartToMetric = (
  chart: BaseChart,
  dimKey: string,
  keepSpec: DualSourceSpec,
  targetSlot: string,
  fanOutAllSlots = false
): BaseChart => {
  const stripped = Object.create(Object.getPrototypeOf(chart));
  Object.assign(stripped, chart);
  stripped.settings = { ...chart.settings };
  for (const k of STRIPPABLE_METRIC_KEYS) {
    if (k === dimKey) continue;
    if (fanOutAllSlots) {
      stripped.settings[k] = keepSpec.metric;
    } else {
      delete stripped.settings[k];
    }
  }
  stripped.settings[targetSlot] = keepSpec.metric;
  return stripped;
};

const planSpec = (primary: DualSourceSpec, secondary: DualSourceSpec): DualSourceSpec | null =>
  primary.source === "plan" ? primary : secondary.source === "plan" ? secondary : null;

const exploreSpec = (primary: DualSourceSpec, secondary: DualSourceSpec): DualSourceSpec | null =>
  primary.source === "explore" ? primary : secondary.source === "explore" ? secondary : null;

export default class DualChartDataComposer {
  async compose(args: DualComposeArgs): Promise<ChartDataRecord[]> {
    const {
      chart,
      project,
      fetchExplore,
      dimKey,
      exploreMetricSlot,
      primary,
      secondary,
      isOvertime,
      filters,
      groupKey,
    } = args;
    const dimField = (chart.settings?.[dimKey] as string) ?? "";
    const groupField = groupKey ? String(chart.settings?.[groupKey] ?? "") : "";
    const plan = planSpec(primary, secondary);
    const expl = exploreSpec(primary, secondary);

    const [planRows, exploreRows] = await Promise.all([
      plan && project ? this.fetchPlan(chart, project, isOvertime, filters) : Promise.resolve([]),
      expl
        ? isOvertime
          ? this.fetchExploreOvertime(chart, fetchExplore, dimKey, expl, exploreMetricSlot, project, groupField)
          : this.fetchExplore(chart, fetchExplore, dimKey, expl, exploreMetricSlot, project)
        : Promise.resolve([]),
    ]);

    const expectedMetricNames = this.getExpectedMetricNames(chart);
    const merged = this.merge(planRows, exploreRows, dimField, plan, expl, expectedMetricNames, groupField);
    if (isOvertime) {
      merged.sort((a, b) => String(a[dimField] ?? "").localeCompare(String(b[dimField] ?? "")));
    }
    return merged;
  }

  private async fetchPlan(
    chart: BaseChart,
    project: Project,
    isOvertime?: boolean,
    filters?: ProjectOutputFilterParams | null
  ): Promise<ChartDataRecord[]> {
    const api = new ProjectWidgetApi();
    return isOvertime ? api.getChartDataOvertime(project, chart, filters) : api.getChartData(project, chart);
  }

  private async fetchExplore(
    chart: BaseChart,
    fetchExplore: (body: unknown) => Promise<unknown>,
    dimKey: string,
    spec: DualSourceSpec,
    exploreMetricSlot: string,
    project: Project | null
  ): Promise<ChartDataRecord[]> {
    const stripped = stripChartToMetric(chart, dimKey, spec, exploreMetricSlot, true);
    const body = new WidgetApi().getExploreChartRequestBody(stripped, project);
    const res = (await fetchExplore(body)) as { data?: ChartDataRecord[] } | undefined;
    return res?.data ?? [];
  }

  private async fetchExploreOvertime(
    chart: BaseChart,
    fetchExplore: (body: unknown) => Promise<unknown>,
    granularityKey: string,
    spec: DualSourceSpec,
    exploreMetricSlot: string,
    project: Project | null,
    groupField: string
  ): Promise<ChartDataRecord[]> {
    const granularityValue = String(chart.settings?.[granularityKey] ?? "month");
    const stripped = stripChartToMetric(chart, granularityKey, spec, exploreMetricSlot, true);
    stripped.settings[granularityKey] = "day";
    const body = new WidgetApi().getExploreChartRequestBody(stripped, project);
    const res = (await fetchExplore(body)) as { data?: ChartDataRecord[] } | undefined;
    return this.bucketExploreRows(res?.data ?? [], granularityValue, spec.metric, groupField);
  }

  private bucketExploreRows(
    dailyRows: ChartDataRecord[],
    granularityValue: string,
    metric: string | string[],
    groupField = ""
  ): ChartDataRecord[] {
    const metrics = asMetrics(metric);
    const byBucket = new Map<string, { bucket: string; group: string; values: Record<string, number> }>();
    for (const row of dailyRows) {
      const dayStr = String(row["day"] ?? row[granularityValue] ?? "");
      const dayDate = toDate(dayStr);
      const bucket =
        dayDate && granularityValue === "week"
          ? isoKey(wStart(dayDate))
          : dayDate && granularityValue === "quarter"
          ? isoKey(qStart(dayDate))
          : dayDate && granularityValue === "month"
          ? isoKey(mStart(dayDate))
          : dayStr;
      const group = groupField ? String(row[groupField] ?? "") : "";
      const bucketKey = groupField ? `${bucket}||${group}` : bucket;
      const entry = byBucket.get(bucketKey) ?? { bucket, group, values: {} };
      for (const m of metrics) {
        entry.values[m] = (entry.values[m] ?? 0) + (Number(row[m]) || 0);
      }
      byBucket.set(bucketKey, entry);
    }
    return Array.from(byBucket.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, entry]) => ({
        [granularityValue]: entry.bucket,
        ...(groupField ? { [groupField]: entry.group } : {}),
        ...entry.values,
      }));
  }

  private merge(
    planRows: ChartDataRecord[],
    exploreRows: ChartDataRecord[],
    dimField: string,
    plan: DualSourceSpec | null,
    expl: DualSourceSpec | null,
    expectedMetricNames: string[],
    groupField = ""
  ): ChartDataRecord[] {
    const byDim = new Map<string, ChartDataRecord>();
    const keyOf = (r: ChartDataRecord): string =>
      groupField ? `${String(r[dimField] ?? "")}||${String(r[groupField] ?? "")}` : String(r[dimField] ?? "");

    for (const r of planRows) {
      byDim.set(keyOf(r), { ...r });
    }

    if (expl) {
      for (const r of exploreRows) {
        if (otherValues.includes(String(r[dimField] ?? ""))) continue;
        const key = keyOf(r);
        const planRow = byDim.get(key);
        if (planRow) {
          for (const m of asMetrics(expl.metric)) {
            planRow[m] = r[m] ?? planRow[m] ?? null;
          }
        } else {
          byDim.set(key, { ...r });
        }
      }
    }

    const planMetrics = plan ? asMetrics(plan.metric) : [];
    const explMetrics = expl ? asMetrics(expl.metric) : [];
    for (const row of byDim.values()) {
      for (const m of [...planMetrics, ...explMetrics, ...expectedMetricNames]) {
        if (!(m in row)) row[m] = null;
      }
    }
    return Array.from(byDim.values());
  }

  private getExpectedMetricNames(chart: BaseChart): string[] {
    const shape = chartsSettings[chart.chartName]?.planDualSettings as
      | Record<string, { dictionary?: string }>
      | undefined;
    if (!shape) return [];
    const names: string[] = [];
    for (const key of Object.keys(shape)) {
      if (shape[key]?.dictionary !== "displayMetrics") continue;
      const value = chart.settings?.[key];
      if (typeof value === "string" && value.length) {
        names.push(value);
      } else if (Array.isArray(value)) {
        names.push(...value.filter((v): v is string => typeof v === "string" && v.length > 0));
      }
    }
    return names;
  }
}
